<template>
  <div ref="wrapper" style="height: 100%; width: 100%">
    <div
      v-if="
        props.field == 'cost' &&
        preferenceStore.preferences?.currency_code !== 'EUR'
      "
      class="absolute left-0 top-0 flex items-center gap-2 text-gray-600 dark:text-gray-400">
      <Info :size="14" />
      <span>{{ getExpenseCurrencyInfo() }}</span>
    </div>

    <div
      v-show="isDataExisting || loading"
      style="height: 100%; width: 100%"
      class="information-chart">
      <canvas
        id="information-line-chart"
        height="100%"
        width="100%"
        style="height: 100% !important; width: 100% !important"
        @click="clickGraph()"></canvas>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import {
    GraphPeriodUnit,
    MetadataQueryField,
    StatisticsService,
    StatisticType,
  } from '@/client'
  import { useGlobalStore } from '@/stores/globalStore'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import { useSessionStore } from '@/stores/sessionStore'
  import { Info } from 'lucide-vue-next'
  import { useRouter } from 'vue-router'

  type EmptyStateType = 'none' | 'noStats' | 'filteredEmpty'

  const emit = defineEmits<{
    (e: 'empty-state-change', v: EmptyStateType): void
  }>()

  const props = defineProps({
    applicationId: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },
    chartColor: {
      type: String as PropType<string | undefined | null>,
      default: undefined,
    },
    field: {
      type: String as PropType<MetadataQueryField>,
      required: true,
    },
    hideAxes: {
      type: Boolean,
      default: false,
    },
    statisticType: {
      type: String as PropType<StatisticType>,
      default: StatisticType.COMPANY_STATISTIC,
    },
  })

  const requestedRange = defineModel<[Date, Date]>('requestedRange', {
    required: true,
  })

  const router = useRouter()

  const i18n = useI18n()
  const preferenceStore = usePreferenceStore()
  const globalStore = useGlobalStore()
  const sessionStore = useSessionStore()

  const chartRef = ref()

  interface ChartData {
    x: Date
    y: number
  }

  const chartData = ref<ChartData[]>([])
  const loading = ref(false)

  const isDataExisting = computed(() => chartData.value.length > 0)

  watch(
    [
      () => requestedRange.value?.[0]?.getTime(),
      () => requestedRange.value?.[1]?.getTime(),
      () => props.field,
      () => props.applicationId,
      () => props.statisticType,
    ],
    () => {
      fetchStatistics(props.field)
    },
    { immediate: true }
  )

  // Fetch statistics
  async function fetchStatistics(field: MetadataQueryField) {
    loading.value = true
    try {
      const response = await StatisticsService.getStatisticApiV1StatisticsGet({
        fromDate: requestedRange.value[0].toISOString(),
        tillDate: requestedRange.value[1].toISOString(),
        type: props.statisticType,
        field,
        periodUnit: GraphPeriodUnit.DAY,
        periodStepSize: 1,
        softwareId: props.applicationId,
      })

      chartData.value = response.map((item) => ({
        x: new Date(item[0] * 1000),
        y: Math.round(item[1] * 100) / 100,
      }))

      if (chartData.value.length > 0) {
        emit('empty-state-change', 'none')
      } else {
        const anyResponse =
          await StatisticsService.getStatisticApiV1StatisticsGet({
            fromDate: new Date(2000, 0, 1).toISOString(),
            tillDate: new Date().toISOString(),
            type: props.statisticType,
            field,
            periodUnit: GraphPeriodUnit.DAY,
            periodStepSize: 1,
            softwareId: props.applicationId,
          })
        emit(
          'empty-state-change',
          (anyResponse?.length ?? 0) > 0 ? 'filteredEmpty' : 'noStats'
        )
      }
    } finally {
      loading.value = false
    }
  }

  // Chart Data
  const data = computed(() => ({
    datasets: [
      {
        data: chartData.value,
        pointHitRadius: 10, // widen the hit area of the points (hover)
        fill: false,
        borderColor: props.chartColor,
        cubicInterpolationMode: 'monotone',
        outerGlowColor: props.chartColor,
        outerGlowWidth: 40,
      },
    ],
  }))

  const scales = () => {
    if (props.hideAxes) {
      return {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              callback: () => '',
            },
            gridLines: {
              display: false,
              drawBorder: false,
            },
          },
        ],
        xAxes: [
          {
            offset: false,
            type: 'time',
            time: {
              unit: 'day',
              unitStepSize: 1,
              displayFormats: {
                day: 'MMM DD',
              },
            },
            ticks: {
              beginAtZero: true,
              callback: () => '',
            },
            gridLines: {
              display: false,
              drawBorder: false,
            },
          },
        ],
      }
    } else {
      return {
        xAxes: [
          {
            gridLines: {
              display: false,
              drawBorder: false,
            },
            offset: false,
            type: 'time',
            time: {
              unit: 'day',
              unitStepSize: 7,
              displayFormats: {
                day: 'DD.MM.YY',
              },
            },
            ticks: {
              maxRotation: 90,
              minRotation: 45,
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              precision: 0,
              callback: function (value: number) {
                // If the active tab is 'spends', prefix the value with €
                return props.field === 'cost'
                  ? `${getCurrencySymbol()}${value.toFixed(2)}`
                  : Math.round(value).toString()
              },
            },
          },
        ],
      }
    }
  }

  function getCurrencySymbol(): string {
    if (sessionStore.currentCompany?.region === 'US') return '$'
    return '€'
  }

  function getExpenseCurrencyInfo(): string {
    return i18n.t('historicalExpenseCurrencyInfo', {
      currency: getCurrencySymbol(),
    })
  }
  let delayed: boolean
  const options = computed(() => ({
    type: 'shadowedLineChart',
    responsive: true,
    maintainAspectRatio: false,
    data: data.value,
    plugins: {
      title: {
        display: false,
      },
    },
    legend: {
      display: true,
      labels: {
        generateLabels: () => {
          return [{ text: '' }]
        },
        boxWidth: 0,
        boxHeight: 0,
      },
    },
    interaction: {
      intersect: false,
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    layout: {
      padding: {
        top: 2,
      },
    },
    scales: scales(),
    animation: {
      duration: 1000,
      onComplete: () => {
        delayed = true
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delay: (context: any) => {
        let delay = 0
        if (context.type === 'data' && context.mode === 'default' && !delayed) {
          delay = context.dataIndex * 150 + context.datasetIndex * 100
        }
        return delay
      },
    },
  }))

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let chart: any = null

  // Map activeTabName to route names or paths
  const tabRouteMap: Record<MetadataQueryField, { name: string }> = {
    [MetadataQueryField.ACCOUNT_AMOUNT]: { name: 'Users' },
    [MetadataQueryField.LICENCE_AMOUNT]: { name: 'Accounts' },
    [MetadataQueryField.SOFTWARE_AMOUNT]: { name: 'Applications' },
    [MetadataQueryField.COST]: { name: 'Applications' },
    [MetadataQueryField.OPTIMIZATION_AMOUNT]: { name: 'Applications' },
    [MetadataQueryField.SAVING_AMOUNT]: { name: 'Applications' },
  }

  function clickGraph() {
    router.push(tabRouteMap[props.field])
  }

  watch([data, () => props.field], () => {
    if (chart) {
      chart.data.datasets[0].data = chartData.value
      chart.update()
    }
  })

  watch(
    () => props.chartColor,
    () => {
      if (chart) {
        chart.data.datasets[0].borderColor = props.chartColor
        chart.data.datasets[0].outerGlowColor = props.chartColor
        chart.update()
      }
    }
  )

  watch(
    () => isDataExisting.value,
    (exists) => {
      if (exists) nextTick(() => initChart())
    }
  )

  function initChart() {
    const el = document.getElementById(
      'information-line-chart'
    ) as HTMLCanvasElement | null
    if (!el || chart) return
    const ctx = el.getContext('2d')
    if (ctx)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore 2552
      // eslint-disable-next-line no-undef
      chart = new Chart(ctx, {
        type: 'line',
        data: data.value,
        options: options.value,
      })
  }

  onMounted(() => {
    document.addEventListener('sidebar-collapse-state-changed', () => {
      setTimeout(() => chart?.resize(), 300)
    })
    if (isDataExisting.value) initChart()
  })

  onUnmounted(() => {
    document.removeEventListener('sidebar-collapse-state-changed', () =>
      chart?.resize()
    )
    if (chart) chart.destroy()
  })
</script>

<style lang="scss">
  .information-chart {
    canvas {
      height: 100% !important;
    }
  }
</style>
